import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography
} from "@mui/material";
import React from "react";
import { Reveal, StaggerGroup } from "src/theme/animation";

const VisionAndPrinciples: React.FC = () => {
    const corePrinciples = [
        {
            title: "Asset-Based, Not Deficit-Based",
            text: "We do not “fix people.” We surface strengths, patterns, and growth."
        },
        {
            title: "Human Judgment Always Comes First",
            text: "Technology supports reflection — it never replaces relationships."
        },
        {
            title: "Student Agency Is Non-Negotiable",
            text: "SMSP is for the people, not about them. "
        },
        {
            title: "Transparency Builds Trust",
            text: "Growth is visible, contextual, and shared with intention."
        }
    ];

    const pillars = [
        {
            title: "Student-First Growth & Agency",
            subtitle: "The individual is the primary user and the primary beneficiary.",
            points: [
                "Understand the platform exists for them",
                "Have space to reflect, track their growth, and use their voice",
                "Experience feedback as support, not judgment",
                "Build confidence through awareness and communication"
            ],
            tagline: '"Ask. Listen. Celebrate!!!"'
        },
        {
            title: "Asset-Based Reflection & Universal Design for Learning",
            subtitle: "Growth starts with what's already working.",
            points: [
                "Asset-based framing to all feedback",
                "Universal Design for Learning (UDL) to meet students where they are",
                "Multiple ways to engage, reflect, and demonstrate understanding"
            ],
            note: "Reflection is treated as a skill, not a byproduct. The system listens for personal growth over time, engagement and comprehension, recall and understanding, and coach instruction quality—all without labeling, diagnosing, or ranking students."
        },
        {
            title: "Safe, Ethical, Human-Centered Technology",
            subtitle: "Safety is foundational — not a feature.",
            points: [
                "No diagnoses",
                "No mental health claims",
                "No automated judgments",
                "No external micromanagement"
            ],
            note: "Data principles: Voice stored temporarily (30 days max); analysis retained, audio deleted by default; full storage only with lead partner permission; access defined by LOI; admin insights are anonymized only. Our technology exists to protect trust, not erode it."
        },
        {
            title: "Transparent Ecosystem for Human Development",
            subtitle: "Real growth happens in the community.",
            points: [
                "Indivudals see their own growth",
                "Coaches gain insight into instruction and engagement",
                "Guardians understand progress without overreach",
                "Organizations see trends without individual exposure"
            ],
            note: "This transparency prevents misinterpretation, reduces unintended consequences, and strengthens relationships."
        },
        {
            title: "Measurable Outcomes for Institutions, Without Dehumanization",
            subtitle: "Institutions need outcomes. Individual need dignity. SMSP delivers both.",
            points: [
                "Clear short-, medium-, and long-term outcome tracking",
                "Pre- and post-assessments aligned to confidence, regulation, and communication",
                "Well-being indicators without clinical claims",
                "An ethical, scalable model for individual development"
            ],
            note: "Institutions gain confidence in impact without reducing individuals to data points."
        }
    ];

    const promiseItems = [
        "To Individuals: Your voice matters. Your growth is yours.",
        "To Guardians: Insight without intrusion.",
        "To Coaches: Feedback that strengthens instruction.",
        "To Institutions: Measurable outcomes, responsibly delivered."
    ];

    return (
        <Box sx={{ mb: 8 }}>
            <Reveal variant="fadeUp">
                <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                        textAlign: "center",
                        mb: 4,
                        fontWeight: "bold"
                    }}
                >
                    Grounded in science. Built from experience.
                </Typography>
            </Reveal>

            {/* Vision + Core Principles: side-by-side on large, stacked on medium and below */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    mb: 6,
                    "& > *": {
                        flex: { xs: "1 1 100%", lg: "1 1 calc(50% - 16px)" },
                        minWidth: 0
                    }
                }}
            >
                {/* Vision */}
                <Reveal variant="fadeUp">
                    <Box
                    sx={(theme) => ({
                        backgroundColor: theme.palette.primary.main,
                        backgroundImage: `radial-gradient(circle at 25% 25%, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.light} 100%)`,
                        color: "primary.contrastText",
                        p: 3,
                        borderRadius: 2,
                        "& .MuiTypography-root": { color: "inherit" }
                    })}
                >
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{ fontWeight: "bold", mb: 2 }}
                    >
                        OUR VISION
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            maxWidth: 800,
                            lineHeight: 1.7,
                            mb: 2,
                            fontWeight: 500
                        }}
                    >
                        SMSP exists to grow confidence by strengthening the quality of reflection, relationships, and communication.
                        We believe confidence is not built through correction or surveillance, but through healthy, consistent
                        interactions that make people feel seen, heard, and valued. SMSP is designed first for the individual --
                        while giving Guardians, Coaches, Managers, and Organizations the visibility, safety, and outcomes they need to support them responsibly.
                        When SMSP works perfectly, people demonstrate measurable improvements in self-regulation, confidence, and communication within six weeks,
                        while the adults and leaders around them gain clearer insight into how to support growth without diminishing agency.
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 800, lineHeight: 1.7, mb: 2 }}>
                        We believe confidence is not built through correction or surveillance, but through{" "}
                        <Box component="span" sx={{ fontWeight: "bold" }}>
                            healthy, consistent interactions
                        </Box>{" "}
                        that make young people feel seen, heard, and valued. SMSP is designed first for students — while giving Guardians, Coaches, and Organizations the visibility, safety, and outcomes they need to support them responsibly.
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 800, lineHeight: 1.7 }}>
                        When SMSP works perfectly, students demonstrate measurable improvements in self-regulation, confidence, and communication within six weeks, while the adults around them gain clearer insight into how to support growth without diminishing agency.
                    </Typography>
                    </Box>
                </Reveal>

                {/* Core Principles */}
                <Reveal variant="fadeUp" delay={0.1}>
                    <Box
                    sx={(theme) => ({
                        backgroundColor: theme.palette.secondary.main,
                        backgroundImage: `radial-gradient(circle at 25% 25%, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.dark} 100%)`,
                        color: "secondary.contrastText",
                        p: 3,
                        borderRadius: 2,
                        "& .MuiTypography-root": { color: "inherit" }
                    })}
                >
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{ fontWeight: "bold", mb: 3 }}
                    >
                        Core Principles (How We Think)
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: "italic", mb: 2, opacity: 0.9 }}>
                        Before the pillars, these guide every decision:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        {corePrinciples.map((principle, idx) => (
                            <Box component="li" key={idx} sx={{ mb: 2 }}>
                                <Typography component="span" sx={{ fontWeight: "bold" }}>
                                    {principle.title}
                                </Typography>
                                <Typography component="span"> — {principle.text}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                </Reveal>
            </Box>

            {/* Five Pillars — collapsible accordions */}
            <Box sx={{ mb: 6 }}>
                <Reveal variant="fadeUp">
                    <Typography
                        variant="h4"
                        component="h3"
                        sx={{
                            textAlign: "center",
                            fontWeight: "bold",
                            mb: 4
                        }}
                    >
                        The Five Pillars of SMSP
                    </Typography>
                </Reveal>
                <StaggerGroup stagger={0.08}>
                    {pillars.map((pillar, idx) => (
                        <Reveal key={idx} variant="fadeUp" staggerIndex={idx}>
                            <Accordion
                        disableGutters
                        sx={{
                            "&:before": { display: "none" },
                            boxShadow: "none",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            "&:first-of-type": { borderTop: "1px solid", borderColor: "divider" }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ "& .MuiAccordionSummary-content": { my: 2 } }}
                        >
                            <Typography variant="h6" component="h4" sx={{ fontWeight: "bold" }}>
                                {pillar.title}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0, pb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1.5, fontStyle: "italic" }}>
                                {pillar.subtitle}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 1 }}>
                                {pillar.points.map((point, i) => (
                                    <Box component="li" key={i}>
                                        <Typography variant="body2">{point}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            {pillar.tagline && (
                                <Typography variant="body2" sx={{ fontStyle: "italic", color: "primary.main" }}>
                                    {pillar.tagline}
                                </Typography>
                            )}
                            {pillar.note && (
                                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                                    {pillar.note}
                                </Typography>
                            )}
                        </AccordionDetails>
                            </Accordion>
                        </Reveal>
                    ))}
                </StaggerGroup>
            </Box>

            {/* The SMSP Promise */}
            <Reveal variant="fadeUp">
                <Box>
                <Typography variant="h5" component="h3" sx={{ fontWeight: "bold", mb: 2 }}>
                    The SMSP Promise
                </Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {promiseItems.map((item, idx) => (
                        <Box component="li" key={idx} sx={{ mb: 1 }}>
                            <Typography variant="body2">{item}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
            </Reveal>
        </Box>
    );
};

export default VisionAndPrinciples;
